import { SignalProtocolStore } from "../store/SignalProtocolStore";
import {
  storePlaintext,
  getPlaintext,
  pruneOldMessages,
} from "../store/MessagePlaintextStore";
import {
  WEB_DEVICE_ID,
  decryptMessage,
  encryptMessage,
  establishSession,
} from "../crypto/session";
import {
  encodeEnvelope,
  decodeEnvelope,
  isE2EEEnvelope,
  isGroupEnvelope,
  encodeGroupEnvelope,
  decodeGroupEnvelope,
} from "../crypto/envelope";
import {
  IdentityVerificationStore,
  IdentityChangedError,
  canSend,
} from "../identity-verification/IdentityVerification";

// Local alias of WEB_DEVICE_ID. Used both as the sender-side identifier for
// THIS web install AND as the default recipient device id when the caller
// does not specify one — current peers are single-device so the two roles
// share the same value. Single source of truth still lives in
// crypto/session.ts.
const DEFAULT_DEVICE_ID = WEB_DEVICE_ID;
const MEMORY_CACHE_MAX = 500;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

let _store: SignalProtocolStore | null = null;
let _currentUserId: string | null = null;

const _memoryCache = new Map<string, string>();

export function getE2EEStore(): SignalProtocolStore {
  if (!_store) {
    _store = new SignalProtocolStore();
  }
  return _store;
}

export function setE2EECurrentUserId(userId: string): void {
  _currentUserId = userId;
  pruneOldMessages().catch(() => {});
}

export function getE2EECurrentUserId(): string | null {
  return _currentUserId;
}

function memoryCachePut(key: string, value: string): void {
  if (_memoryCache.size >= MEMORY_CACHE_MAX) {
    const first = _memoryCache.keys().next().value;
    if (first) _memoryCache.delete(first);
  }
  _memoryCache.set(key, value);
}

async function persistPlaintext(
  encryptedContent: string,
  plaintext: string,
): Promise<void> {
  memoryCachePut(encryptedContent, plaintext);
  storePlaintext(encryptedContent, plaintext).catch(() => {});
}

// Module-level store instance (lazy, shared across calls).
let _idvStore: IdentityVerificationStore | null = null;

async function getIdvStore(): Promise<IdentityVerificationStore> {
  if (!_idvStore) {
    _idvStore = await IdentityVerificationStore.open();
  }
  return _idvStore;
}

/**
 * Trust gate: throws `IdentityChangedError` if the recipient's identity key
 * has changed and not yet been acknowledged by the user.
 * Called before every outbound encrypt to enforce the 1:1 blocking contract.
 */
async function assertCanSend(recipientUserId: string): Promise<void> {
  const idvStore = await getIdvStore();
  const record = await idvStore.get(recipientUserId);
  if (record && !canSend(record)) {
    throw new IdentityChangedError(recipientUserId);
  }
}

async function encryptForSingleRecipient(
  recipientUserId: string,
  plaintext: string,
  recipientDeviceId = DEFAULT_DEVICE_ID,
): Promise<string> {
  // Trust gate: refuse encryption if the recipient's identity key has
  // changed and not yet been acknowledged.
  await assertCanSend(recipientUserId);

  const store = getE2EEStore();

  const sessionEstablished = await establishSession(
    store,
    recipientUserId,
    recipientDeviceId,
  );
  if (!sessionEstablished) {
    throw new Error(
      `Cannot establish E2EE session with ${recipientUserId}`,
    );
  }

  const plaintextBytes = textEncoder.encode(plaintext);
  const msg = await encryptMessage(
    store,
    recipientUserId,
    recipientDeviceId,
    plaintextBytes,
  );

  let bodyBytes: Uint8Array;
  if (!msg.body) {
    bodyBytes = new Uint8Array(0);
  } else if (typeof msg.body === "string") {
    bodyBytes = new Uint8Array(msg.body.length);
    for (let i = 0; i < msg.body.length; i++) {
      bodyBytes[i] = msg.body.charCodeAt(i);
    }
  } else {
    bodyBytes = new Uint8Array(msg.body as unknown as ArrayBuffer);
  }

  const kind = msg.type === 3 ? "pk" : "msg";
  return encodeEnvelope(
    kind as "pk" | "msg",
    DEFAULT_DEVICE_ID,
    bodyBytes,
  );
}

export async function e2eeEncrypt(
  recipientUserId: string,
  plaintext: string,
  recipientDeviceId = DEFAULT_DEVICE_ID,
): Promise<string> {
  const envelope = await encryptForSingleRecipient(
    recipientUserId,
    plaintext,
    recipientDeviceId,
  );

  await persistPlaintext(envelope, plaintext);
  return envelope;
}

export async function e2eeEncryptForChannel(
  recipientUserIds: string[],
  plaintext: string,
): Promise<string> {
  const recipients: Record<string, string> = {};

  const results = await Promise.allSettled(
    recipientUserIds.map(async (userId) => {
      const envelope = await encryptForSingleRecipient(userId, plaintext);
      return { userId, envelope };
    }),
  );

  for (const result of results) {
    if (result.status === "fulfilled") {
      recipients[result.value.userId] = result.value.envelope;
    } else {
      console.warn("[e2ee] Failed to encrypt for a channel member:", result.reason);
    }
  }

  if (Object.keys(recipients).length === 0) {
    throw new Error("Could not encrypt for any channel member");
  }

  const groupEnvelope = encodeGroupEnvelope(recipients);
  await persistPlaintext(groupEnvelope, plaintext);
  return groupEnvelope;
}

export function e2eeLookupSentCache(
  messageContent: string,
): string | undefined {
  return _memoryCache.get(messageContent);
}

async function decryptSingleEnvelope(
  senderUserId: string,
  messageContent: string,
): Promise<{ plaintext: string; encrypted: true } | { encrypted: false }> {
  const envelope = decodeEnvelope(messageContent);
  if (!envelope) {
    return { encrypted: false };
  }

  const store = getE2EEStore();
  const isPreKey = envelope.kind === "pk";

  const plaintextBytes = await decryptMessage(
    store,
    senderUserId,
    envelope.senderDeviceId,
    envelope.ciphertext,
    isPreKey,
  );
  return {
    plaintext: textDecoder.decode(plaintextBytes),
    encrypted: true,
  };
}

export async function e2eeDecrypt(
  senderUserId: string,
  messageContent: string,
): Promise<{ plaintext: string; encrypted: true } | { encrypted: false }> {
  if (!isE2EEEnvelope(messageContent)) {
    return { encrypted: false };
  }

  // Fast path: in-memory cache
  const memCached = _memoryCache.get(messageContent);
  if (memCached !== undefined) {
    return { plaintext: memCached, encrypted: true };
  }

  // Persistent path: IndexedDB
  const persisted = await getPlaintext(messageContent);
  if (persisted !== undefined) {
    memoryCachePut(messageContent, persisted);
    return { plaintext: persisted, encrypted: true };
  }

  // Signal Protocol decryption
  let result: { plaintext: string; encrypted: true } | { encrypted: false };

  if (isGroupEnvelope(messageContent)) {
    const recipientMap = decodeGroupEnvelope(messageContent);
    if (!recipientMap) return { encrypted: false };

    const myUserId = _currentUserId;
    if (!myUserId) {
      console.warn("[e2ee] Current user ID not set, cannot decrypt group message");
      return { encrypted: false };
    }

    const myEnvelope = recipientMap[myUserId];
    if (!myEnvelope) {
      return { encrypted: false };
    }

    result = await decryptSingleEnvelope(senderUserId, myEnvelope);
  } else {
    result = await decryptSingleEnvelope(senderUserId, messageContent);
  }

  // Persist the decrypted plaintext for future page loads
  if (result.encrypted) {
    await persistPlaintext(messageContent, result.plaintext);
  }

  return result;
}

export function e2eeIsEncrypted(messageContent: string): boolean {
  return isE2EEEnvelope(messageContent);
}

// Re-export identity-verification surface so call sites don't need a separate
// deep import.
export {
  IdentityVerificationStore,
  IdentityChangedError,
  canSend,
  pubKeyToHex,
} from "../identity-verification/IdentityVerification";
export type {
  IdentityTrustState,
  IdentityVerificationRecord,
} from "../identity-verification/IdentityVerification";
